import { useState, useCallback, useLayoutEffect, useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { produce } from "immer";

/**
 * Line Number Alignment with Absolute Positioning
 *
 * This module provides utilities for aligning line numbers with their corresponding
 * content using CSS position:absolute. This approach works in all browsers including
 * Safari, which has bugs with CSS Grid row subgrid.
 *
 * ## How It Works:
 *
 * 1. The container (QuoteGroup) has `position: relative`
 * 2. Line numbers are positioned with `position: absolute`
 * 3. JavaScript measures the Y position of target elements (QuotePartsTable)
 * 4. The `top` value of each line number is set to match its target
 *
 * ## Key Components:
 *
 * - `useAbsoluteLineAlignment()` - Main hook for alignment
 * - Target elements call `registerTarget(lineNo)` to register for alignment
 * - Line numbers use `getLineNumberStyle(lineNo)` for their position
 *
 * ## Example Usage:
 *
 * ```tsx
 * const { containerRef, registerTarget, getLineNumberStyle } = useAbsoluteLineAlignment();
 *
 * return (
 *   <QuoteGroup ref={containerRef}>
 *     <QuoteCard>
 *       <QuotePartsTable ref={registerTarget("10")} quoteLine={line10} />
 *     </QuoteCard>
 *     <LineNumberCell style={getLineNumberStyle("10")}>10</LineNumberCell>
 *   </QuoteGroup>
 * );
 * ```
 */

interface PositionMap {
  [lineNo: string]: number;
}

/**
 * Hook that calculates absolute positions for line numbers.
 *
 * Uses ResizeObserver to automatically update positions when content changes.
 *
 * @returns Object with:
 *   - `containerRef`: Ref to attach to the container (QuoteGroup)
 *   - `registerTarget`: Callback to register a target element for a line number
 *   - `getLineNumberStyle`: Function that returns CSS for a line number's position
 */
export const useAbsoluteLineAlignment = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const targetRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [positions, setPositions] = useState<PositionMap>({});

  // Calculate positions for all registered targets
  const calculatePositions = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerScrollTop = containerRef.current.scrollTop;

    // Use immer's produce - it returns the same reference if values don't change,
    // preventing infinite re-render loops from ResizeObserver
    setPositions(
      produce((draft) => {
        targetRefs.current.forEach((element, lineNo) => {
          const elementRect = element.getBoundingClientRect();
          const top = elementRect.top - containerRect.top + containerScrollTop;
          draft[lineNo] = top;
        });
      }),
    );
  }, []);

  // Calculate on mount and when refs change
  useLayoutEffect(() => {
    calculatePositions();
  }, [calculatePositions]);

  // ResizeObserver for dynamic updates
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const observer = new ResizeObserver(() => {
      calculatePositions();
    });

    // Observe the container
    observer.observe(containerRef.current);

    // Also observe all registered targets
    targetRefs.current.forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [calculatePositions]);

  // Register a target element for alignment
  const registerTarget = useCallback(
    (lineNo: string) => (element: HTMLElement | null) => {
      if (element) {
        targetRefs.current.set(lineNo, element);
        // Recalculate after registration
        // Use setTimeout to ensure the element is fully rendered
        setTimeout(calculatePositions, 0);
      } else {
        targetRefs.current.delete(lineNo);
      }
    },
    [calculatePositions],
  );

  // Get style for a line number cell
  const getLineNumberStyle = useCallback(
    (lineNo: string): CSSProperties => ({
      position: "absolute" as const,
      top: positions[lineNo] ?? 0,
      left: 0,
    }),
    [positions],
  );

  return {
    containerRef,
    registerTarget,
    getLineNumberStyle,
    // Expose recalculate for manual triggers (e.g., after expand/collapse)
    recalculate: calculatePositions,
  };
};

/**
 * Simple alignment info for debugging
 */
export const useAlignmentDebug = () => {
  const [debugInfo, setDebugInfo] = useState<string>("");

  const logAlignment = useCallback((lineNo: string, targetTop: number, containerTop: number) => {
    setDebugInfo(
      (prev) =>
        `${prev}\nLine ${lineNo}: target=${targetTop.toFixed(0)}, container=${containerTop.toFixed(0)}, offset=${(
          targetTop - containerTop
        ).toFixed(0)}`,
    );
  }, []);

  return { debugInfo, logAlignment };
};
